import { Box, Button, Stack, Typography } from '@mui/material';
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { FormColorInputDisplay } from '../../form-field-components/new-and-edit/form-color-input';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { IReportPeriodWebpageSetting } from '../../../../shared/services/report-period-webpage-setting/report-period-webpage-setting.model';
import ReportPeriodWebpageSettingService from '../../../../shared/services/report-period-webpage-setting/report-period-webpage-setting.service';
import { FormImageInputDisplay } from '../../form-field-components/new-and-edit/form-image-input';
import { GaiaWebPageCoverImageDialog } from '../../../dialogs/reporting/gaia-web-page-cover-image-dialog';
import {
  GAIA_REPORT_CHART_SERIES_COUNT,
  buildGaiaReportColorPalette,
  getReportChartSeriesColors,
} from '../../../../modules/public-reports/utils/gaia-report-color-palette';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils';

export const ReportingGaiaWebPageSettingsForm: FunctionComponent<{
  reportPeriodWebpageSettings: IReportPeriodWebpageSetting
  setReportPeriodWebpageSettings: React.Dispatch<React.SetStateAction<any>>
  id: string
}> = ({
  reportPeriodWebpageSettings,
  setReportPeriodWebpageSettings,
  id
}) => {
    //Services
    const reportPeriodWebpageSettingService = new ReportPeriodWebpageSettingService()
    //Context
    const userDetailsContext = useContext(UserDetailsContext)
    const userNotificationsContext = useContext(UserNotificationsContext)
    const userDetails = userDetailsContext.userDetails
    //State
    const [coverImageFileData, setCoverImageFileData] = useState([]);
    const [showDefaultImages, setShowDefaultImages] = useState<boolean>(false);
    const [editField, setEditField] = useState<string>('');
    const [localThemeColor, setLocalThemeColor] = useState<string>(reportPeriodWebpageSettings?.themeColor || '');
    const [localTextColor, setLocalTextColor] = useState<string>(reportPeriodWebpageSettings?.textColor || '');
    const [localPaletteOverrides, setLocalPaletteOverrides] = useState(() => ({
      light: reportPeriodWebpageSettings?.paletteOverrides?.light ?? '',
      dark: reportPeriodWebpageSettings?.paletteOverrides?.dark ?? '',
      veryLight: reportPeriodWebpageSettings?.paletteOverrides?.veryLight ?? '',
      veryDark: reportPeriodWebpageSettings?.paletteOverrides?.veryDark ?? '',
    }));
    const [localBarChartColors, setLocalBarChartColors] = useState<string[]>(() =>
      Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, (_, i) =>
        typeof reportPeriodWebpageSettings?.paletteOverrides?.barChartColors?.[i] === 'string'
          ? (reportPeriodWebpageSettings?.paletteOverrides?.barChartColors?.[i] as string)
          : ''
      )
    );
    const [localCoverImage, setLocalCoverImage] = useState<string>(reportPeriodWebpageSettings?.coverImageFilepath || '');

    const themeBaseColor =
      reportPeriodWebpageSettings?.themeColor || userDetails?.organisation?.primaryColor || '#70942c';

    const effectivePalette = buildGaiaReportColorPalette(
      themeBaseColor,
      reportPeriodWebpageSettings?.paletteOverrides ?? null
    );

    const effectiveChartSeries = getReportChartSeriesColors(
      themeBaseColor,
      reportPeriodWebpageSettings?.paletteOverrides ?? null
    );

    useEffect(() => {
      setLocalPaletteOverrides({
        light: reportPeriodWebpageSettings?.paletteOverrides?.light ?? '',
        dark: reportPeriodWebpageSettings?.paletteOverrides?.dark ?? '',
        veryLight: reportPeriodWebpageSettings?.paletteOverrides?.veryLight ?? '',
        veryDark: reportPeriodWebpageSettings?.paletteOverrides?.veryDark ?? '',
      });
      setLocalBarChartColors(
        Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, (_, i) => {
          const v = reportPeriodWebpageSettings?.paletteOverrides?.barChartColors?.[i];
          return typeof v === 'string' ? v : '';
        })
      );
    }, [reportPeriodWebpageSettings]);

    const mergePaletteOverride = (
      key: 'light' | 'dark' | 'veryLight' | 'veryDark',
      raw: string
    ) => {
      const prev = reportPeriodWebpageSettings?.paletteOverrides || {};
      const next: Record<string, unknown> = { ...prev };
      const v = raw.trim();
      if (!v || !/^#[0-9A-Fa-f]{6}$/i.test(v)) {
        delete next[key];
      } else {
        next[key] = v.toLowerCase();
      }
      return Object.keys(next).length ? next : null;
    };

    const mergeBarChartSeriesOverrides = () => {
      const prev = reportPeriodWebpageSettings?.paletteOverrides || {};
      const auto = getMixedColorPalette(themeBaseColor);
      const barChartColors = localBarChartColors.map((s, i) => {
        const t = s.trim();
        if (!t || !/^#[0-9A-Fa-f]{6}$/i.test(t)) return null;
        return t.toLowerCase() === auto[i].toLowerCase() ? null : t.toLowerCase();
      });
      const hasBar = barChartColors.some((x) => x != null);
      const next: Record<string, unknown> = { ...prev };
      if (hasBar) next.barChartColors = barChartColors;
      else delete next.barChartColors;
      return Object.keys(next).length ? next : null;
    };

    const uploadCoverImageHandler = async () => {
      if (!id || coverImageFileData.length === 0) return;

      const form = new FormData();
      form.append("files", coverImageFileData[0]);
      form.append('organisation', JSON.stringify({ organisationId: userDetailsContext.userDetails?.organisation.id }));

      try {
        const settings = await reportPeriodWebpageSettingService.saveCoverImage(id, form)
        setCoverImageFileData([]);
        setReportPeriodWebpageSettings(settings);
        setLocalCoverImage(settings?.coverImageFilepath || '');
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' });
      } catch (err) {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      }
    };

    const submitHandler = async (
      fieldName?: string,
      value?: string | string[] | Record<string, unknown> | null
    ) => {
      const fieldToUpdate = fieldName || editField;
      const valueToUpdate = value;
      if (!fieldToUpdate || valueToUpdate === undefined || !id) return;
      const settingsToUpdate = reportPeriodWebpageSettings || {
        reportPeriodId: id,
        organisationId: userDetails?.organisation?.id
      };
      const updatedSettings = {
        ...settingsToUpdate,
        [fieldToUpdate]: valueToUpdate
      };
      if (fieldToUpdate === 'coverImageFilepath' && valueToUpdate === undefined) {
        updatedSettings.coverImageBucket = undefined;
      }
      try {
        const settings = await reportPeriodWebpageSettingService.updateSettings(id, updatedSettings);
        setReportPeriodWebpageSettings(settings);
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' });
        return settings;
      } catch (err) {
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
        return null;
      }
    };

    const selectDefaultImage = async (imageUrl: string) => {
      setShowDefaultImages(false);
      const settings = await submitHandler('coverImageFilepath', imageUrl);
      if (settings) {
        setLocalCoverImage(settings.coverImageFilepath || '');
      }
    };

    const removeCoverImage = async () => {
      if (editField === 'coverImage') {
        setLocalCoverImage('');
        const settings = await submitHandler('coverImageFilepath', undefined);
        if (settings) {
          setLocalCoverImage(settings.coverImageFilepath || '');
        }
      } else {
        await submitHandler('coverImageFilepath', undefined);
      }
    };

    const cancelEditHandler = () => {
      setEditField('');
      setLocalThemeColor(reportPeriodWebpageSettings?.themeColor || '');
      setLocalTextColor(reportPeriodWebpageSettings?.textColor || '');
      setLocalPaletteOverrides({
        light: reportPeriodWebpageSettings?.paletteOverrides?.light ?? '',
        dark: reportPeriodWebpageSettings?.paletteOverrides?.dark ?? '',
        veryLight: reportPeriodWebpageSettings?.paletteOverrides?.veryLight ?? '',
        veryDark: reportPeriodWebpageSettings?.paletteOverrides?.veryDark ?? '',
      });
      setLocalBarChartColors(
        Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, (_, i) => {
          const v = reportPeriodWebpageSettings?.paletteOverrides?.barChartColors?.[i];
          return typeof v === 'string' ? v : '';
        })
      );
      setLocalCoverImage(reportPeriodWebpageSettings?.coverImageFilepath || '');
    };

    const handleFormSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (editField === 'themeColor') {
        submitHandler('themeColor', localThemeColor);
      } else if (editField === 'primaryColor') {
        submitHandler('textColor', localTextColor);
      } else if (editField === 'paletteLight') {
        submitHandler('paletteOverrides', mergePaletteOverride('light', localPaletteOverrides.light));
      } else if (editField === 'paletteDark') {
        submitHandler('paletteOverrides', mergePaletteOverride('dark', localPaletteOverrides.dark));
      } else if (editField === 'paletteVeryLight') {
        submitHandler('paletteOverrides', mergePaletteOverride('veryLight', localPaletteOverrides.veryLight));
      } else if (editField === 'paletteVeryDark') {
        submitHandler('paletteOverrides', mergePaletteOverride('veryDark', localPaletteOverrides.veryDark));
      } else if (editField?.startsWith('barChart')) {
        submitHandler('paletteOverrides', mergeBarChartSeriesOverrides());
      } else if (editField === 'coverImage') {
        submitHandler('coverImageFilepath', localCoverImage);
      }
    };

    const handleColorThemeChange = (val: string | React.ChangeEvent<HTMLInputElement>) => {
      if (typeof val === 'string') {
        setLocalThemeColor(val);
      } else {
        setLocalThemeColor(val.target.value);
      }
    };

    const handleColorTextChange = (val: string | React.ChangeEvent<HTMLInputElement>) => {
      if (typeof val === 'string') {
        setLocalTextColor(val);
      } else {
        setLocalTextColor(val.target.value);
      }
    };

    const handlePaletteInputChange =
      (key: 'light' | 'dark' | 'veryLight' | 'veryDark') =>
      (val: string | React.ChangeEvent<HTMLInputElement>) => {
        const s = typeof val === 'string' ? val : val.target.value;
        setLocalPaletteOverrides((prev) => ({ ...prev, [key]: s }));
      };

    const handleBarChartInputChange =
      (index: number) => (val: string | React.ChangeEvent<HTMLInputElement>) => {
        const s = typeof val === 'string' ? val : val.target.value;
        setLocalBarChartColors((prev) => {
          const next = [...prev];
          next[index] = s;
          return next;
        });
      };

    /** Clears palette and chart overrides so those colours match automatic values from the current theme colour (same as when overrides are not set). */
    const resetDerivedColoursToThemeDefaults = async () => {
      if (!id) return;
      try {
        const settings = await reportPeriodWebpageSettingService.updateSettings(id, {
          paletteOverrides: null,
        });
        setReportPeriodWebpageSettings(settings);
        setLocalPaletteOverrides({ light: '', dark: '', veryLight: '', veryDark: '' });
        setLocalBarChartColors(Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, () => ''));
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated Successfully' });
      } catch {
        userNotificationsContext.openSnackbar({
          open: true,
          message: 'There was an error, please try again',
          backgroundColor: 'text.primary',
        });
      }
    };

    const sectionTitleSx = {
      fontWeight: 700,
      color: 'text.primary',
      pl: 3,
      pb: 1.5,
    } as const;

    return (
      <Box sx={{ width: '100%' }}>
        <Box component='form' onSubmit={handleFormSubmit}>
          <Typography variant='subtitle1' sx={{ ...sectionTitleSx, pt: 0 }}>
            Report layout
          </Typography>
          <FormImageInputDisplay
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            disabled={editField !== 'coverImage'}
            isTopRow={true}
            regularFieldLabel
            title='Cover image'
            name='coverImage'
            value={editField === 'coverImage' ? localCoverImage : reportPeriodWebpageSettings?.coverImageFilepath}
            onChange={() => { }}
            onImageUpload={uploadCoverImageHandler}
            onImageRemove={removeCoverImage}
            onImageLibrarySelect={() => setShowDefaultImages(true)}
            showImageLibraryButton={true}
            cancelEditHandler={cancelEditHandler}
            fileData={coverImageFileData}
            setFileData={setCoverImageFileData}
            onFileUploadSubmit={uploadCoverImageHandler}
            disableUploadButton={userDetails?.organisation?.name === null ? true : false}
            hideSaveButton={true}
            isBottomRow={true}
          />
          <Typography variant='subtitle1' sx={{ ...sectionTitleSx, pt: 4 }}>
            Colours
          </Typography>
          <FormColorInputDisplay
            isTopRow={true}
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            disabled={editField !== 'themeColor'}
            title='Theme colour'
            name='themeColor'
            tooltip='Sets the defaults for the other colour fields below when they are not customised.'
            value={editField === 'themeColor' ? localThemeColor : reportPeriodWebpageSettings?.themeColor}
            onChange={handleColorThemeChange}
            cancelEditHandler={cancelEditHandler}
            afterHexAction={
              <Button
                type='button'
                variant='text'
                size='small'
                onClick={resetDerivedColoursToThemeDefaults}
                sx={{ textTransform: 'none', fontWeight: 400 }}
              >
                Reset to theme defaults
              </Button>
            }
          />
          <FormColorInputDisplay
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            disabled={editField !== 'primaryColor'}
            title='Text colour'
            name='primaryColor'
            value={editField === 'primaryColor' ? localTextColor : reportPeriodWebpageSettings?.textColor}
            onChange={handleColorTextChange}
            cancelEditHandler={cancelEditHandler}
          />
          <FormColorInputDisplay
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            disabled={editField !== 'paletteLight'}
            title='Primary colour (light)'
            name='paletteLight'
            value={
              editField === 'paletteLight'
                ? localPaletteOverrides.light || effectivePalette.light
                : effectivePalette.light
            }
            onChange={handlePaletteInputChange('light')}
            cancelEditHandler={cancelEditHandler}
          />
          <FormColorInputDisplay
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            disabled={editField !== 'paletteDark'}
            title='Primary colour (dark)'
            name='paletteDark'
            value={
              editField === 'paletteDark'
                ? localPaletteOverrides.dark || effectivePalette.dark
                : effectivePalette.dark
            }
            onChange={handlePaletteInputChange('dark')}
            cancelEditHandler={cancelEditHandler}
          />
          <FormColorInputDisplay
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            disabled={editField !== 'paletteVeryLight'}
            title='Background colour'
            name='paletteVeryLight'
            value={
              editField === 'paletteVeryLight'
                ? localPaletteOverrides.veryLight || effectivePalette.veryLight
                : effectivePalette.veryLight
            }
            onChange={handlePaletteInputChange('veryLight')}
            cancelEditHandler={cancelEditHandler}
          />
          <FormColorInputDisplay
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            disabled={editField !== 'paletteVeryDark'}
            title='Highlight colour'
            name='paletteVeryDark'
            bottomBorder
            isBottomRow
            value={
              editField === 'paletteVeryDark'
                ? localPaletteOverrides.veryDark || effectivePalette.veryDark
                : effectivePalette.veryDark
            }
            onChange={handlePaletteInputChange('veryDark')}
            cancelEditHandler={cancelEditHandler}
          />

          <Typography variant='subtitle1' sx={{ ...sectionTitleSx, pt: 4 }}>
            Chart colours
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'lightGrey.main',
              borderRadius: '10px',
              overflow: 'hidden',
              '& > .MuiFormControl-root': { mb: 0 },
            }}
          >
            {Array.from({ length: GAIA_REPORT_CHART_SERIES_COUNT }, (_, i) => {
              const name = `barChart${i}`;
              const isLast = i === GAIA_REPORT_CHART_SERIES_COUNT - 1;
              return (
                <FormColorInputDisplay
                  key={name}
                  isEditPage={true}
                  editField={editField}
                  setEditField={setEditField}
                  disabled={editField !== name}
                  title={`Chart colour ${i + 1}`}
                  name={name}
                  value={
                    editField === name
                      ? localBarChartColors[i] || effectiveChartSeries[i]
                      : effectiveChartSeries[i]
                  }
                  onChange={handleBarChartInputChange(i)}
                  cancelEditHandler={cancelEditHandler}
                  sideBorders={false}
                  topBorder={i !== 0}
                  bottomBorder={!isLast}
                />
              );
            })}
          </Box>
        </Box>
        {showDefaultImages &&
          <GaiaWebPageCoverImageDialog
            open={showDefaultImages}
            selectDefaultImage={selectDefaultImage}
            setShowDefaultImages={setShowDefaultImages}
          />}
      </Box>
    )
  }